import { useEffect, useState, useRef } from 'react';
import { api } from '../lib/api';
import type { HealthResponse, EquityLatestResponse, RebalanceRun, RegimeScoreItem } from '../types/api';
import { Activity, DollarSign, TrendingUp, Calendar, RefreshCw, ArrowUpRight, ArrowDownRight, Brain } from 'lucide-react';
import { formatDateTimeEastern } from '../lib/dateUtils';
import { CollapsibleCard } from '../components/CollapsibleCard';

const AUTO_REFRESH_INTERVAL = 5 * 60 * 1000;

// ── Regime helpers ──────────────────────────────────────────────────────────

const REGIME_LABEL_META: Record<string, { color: string; short: string }> = {
  TC_BULL_PERSISTENT:    { color: 'bg-emerald-600 text-white',   short: 'Bull Persistent' },
  TC_BEAR_PERSISTENT:    { color: 'bg-red-600 text-white',       short: 'Bear Persistent' },
  MR_BALANCED_PERSISTENT:{ color: 'bg-blue-600 text-white',      short: 'Balanced' },
  BULL_TRANSITION:       { color: 'bg-emerald-400 text-white',   short: 'Bull Transition' },
  BEAR_TRANSITION:       { color: 'bg-red-400 text-white',       short: 'Bear Transition' },
  REGIME_SHIFT_RISK:     { color: 'bg-amber-500 text-white',     short: 'Shift Risk' },
  LOW_CONVICTION:        { color: 'bg-gray-400 text-white',      short: 'Low Conviction' },
};

function RegimeBadge({ label }: { label: string }) {
  const meta = REGIME_LABEL_META[label] ?? { color: 'bg-gray-300 text-gray-700', short: label };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${meta.color}`}>
      {meta.short}
    </span>
  );
}

function ProbBar({ bull, bear }: { bull: number; bear: number }) {
  const sideways = Math.max(0, 1 - bull - bear);
  return (
    <div className="flex h-2 w-full rounded-full overflow-hidden gap-px">
      <div className="bg-emerald-500 transition-all" style={{ width: `${(bull * 100).toFixed(1)}%` }} title={`Bull ${(bull * 100).toFixed(0)}%`} />
      <div className="bg-blue-400 transition-all"   style={{ width: `${(sideways * 100).toFixed(1)}%` }} title={`Sideways ${(sideways * 100).toFixed(0)}%`} />
      <div className="bg-red-500 transition-all"    style={{ width: `${(bear * 100).toFixed(1)}%` }} title={`Bear ${(bear * 100).toFixed(0)}%`} />
    </div>
  );
}

// ── Main Dashboard ──────────────────────────────────────────────────────────

export function Overview() {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [equity, setEquity] = useState<EquityLatestResponse | null>(null);
  const [runs, setRuns] = useState<RebalanceRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [regimeEnabled, setRegimeEnabled] = useState(false);
  const [regimeScores, setRegimeScores] = useState<RegimeScoreItem[]>([]);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('dashboard-auto-refresh');
      if (saved === 'true') setAutoRefresh(true);
    } catch (error) {
      console.warn('Could not access localStorage:', error);
    }
  }, []);

  const loadData = async (isAutoRefresh = false) => {
    try {
      const [healthData, equityData, runsData] = await Promise.all([
        api.getHealth(),
        api.getEquityLatest(),
        api.getRuns(5, true),
      ]);
      setHealth(healthData);
      setEquity(equityData);
      setRuns(runsData);

      // Regime: check feature flag then fetch scores
      try {
        const flags = await api.getRegimeDashboardStatus();
        const engineOn = flags.engine_enabled;
        setRegimeEnabled(engineOn);
        if (engineOn) {
          const scoresData = await api.getRegimeLatestScores({ score_type: 'FINAL_ALLOCATION_SCORE' });
          setRegimeScores(scoresData.scores);
        }
      } catch {
        // Regime engine not available — hide the card silently
        setRegimeEnabled(false);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      if (!isAutoRefresh) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (autoRefresh) {
      intervalRef.current = window.setInterval(() => {
        loadData(true);
      }, AUTO_REFRESH_INTERVAL);
    }
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRefresh]);

  const toggleAutoRefresh = () => {
    const newValue = !autoRefresh;
    setAutoRefresh(newValue);
    try {
      localStorage.setItem('dashboard-auto-refresh', newValue.toString());
    } catch (error) {
      console.warn('Could not save to localStorage:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <span className="text-sm text-gray-600 font-medium">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);

  const formatPercent = (value: number) =>
    `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;

  const formatRunDateTime = (run: RebalanceRun) => {
    const timestamp = run.started_at || run.completed_at;
    if (timestamp) return formatDateTimeEastern(timestamp);
    return run.run_date;
  };

  const formatTokenExpiry = (expiresAt?: string) => {
    if (!expiresAt) return null;
    try {
      const expiryDate = new Date(expiresAt);
      const now = new Date();
      const diffMs = expiryDate.getTime() - now.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      if (diffMs < 0) return 'Expired';
      if (diffDays > 0) return `${diffDays}d ${diffHours}h`;
      if (diffHours > 0) return `${diffHours}h`;
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return diffMinutes > 0 ? `${diffMinutes}m` : 'Less than 1m';
    } catch (e) {
      console.error('Error formatting token expiry:', e);
      return null;
    }
  };

  const dailyChange = equity?.daily_change_pct ?? 0;
  const isPositive = dailyChange >= 0;

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Dashboard</h1>
          <p className="text-gray-600 mt-1 text-base">Monitor your portfolio performance</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2">
              <RefreshCw className={`w-4 h-4 text-gray-600 ${autoRefresh ? 'animate-spin-slow' : ''}`} />
              <span className="text-sm font-medium text-gray-700">Auto-refresh</span>
            </div>
            <button
              id="auto-refresh-toggle"
              type="button"
              role="switch"
              aria-checked={autoRefresh}
              aria-label="Toggle auto-refresh every 5 minutes"
              onClick={toggleAutoRefresh}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                autoRefresh ? 'bg-emerald-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  autoRefresh ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 transition-all duration-200 hover:shadow-lg hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-base font-semibold text-gray-600">Total Equity</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {equity ? formatCurrency(equity.equity) : '--'}
              </p>
            </div>
            <div className="w-14 h-14 rounded-lg flex items-center justify-center bg-emerald-700 shadow-md">
              <DollarSign className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 transition-all duration-200 hover:shadow-lg hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-base font-semibold text-gray-600">Daily Change</p>
              <div className="flex items-center gap-2 mt-2">
                <p className={`text-2xl font-bold ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                  {equity?.daily_change_pct != null ? formatPercent(equity.daily_change_pct) : '--'}
                </p>
                {equity?.daily_change_pct != null && (
                  isPositive
                    ? <ArrowUpRight className="w-6 h-6 text-emerald-600" />
                    : <ArrowDownRight className="w-6 h-6 text-red-600" />
                )}
              </div>
            </div>
            <div className={`w-14 h-14 rounded-lg flex items-center justify-center shadow-md ${isPositive ? 'bg-emerald-600' : 'bg-red-600'}`}>
              <TrendingUp className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 transition-all duration-200 hover:shadow-lg hover-lift">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-base font-semibold text-gray-600">Broker Status</p>
              <div className="flex items-center gap-2 mt-2">
                <div className={`w-3 h-3 rounded-full ${health?.broker_authenticated ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                <p className="text-lg font-bold text-gray-900">
                  {health?.broker_authenticated ? 'Connected' : 'Disconnected'}
                </p>
              </div>
              {health?.broker_authenticated && health?.token_expires_at && (
                <p className="text-sm text-gray-500 mt-1">
                  Expires in: {formatTokenExpiry(health.token_expires_at) || 'N/A'}
                </p>
              )}
            </div>
            <div className={`w-14 h-14 rounded-lg flex items-center justify-center shadow-md ${health?.broker_authenticated ? 'bg-emerald-600' : 'bg-amber-500'}`}>
              <Activity className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 transition-all duration-200 hover:shadow-lg hover-lift">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-base font-semibold text-gray-600">Bot Status</p>
              <div className="flex items-center gap-2 mt-2">
                <div className={`w-3 h-3 rounded-full ${health?.bot_enabled ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                <p className="text-lg font-bold text-gray-900">
                  {health?.bot_enabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
            </div>
            <div className="w-14 h-14 rounded-lg flex items-center justify-center bg-emerald-700 shadow-md">
              <Calendar className="w-7 h-7 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Market Regime card — only shown when regime engine is enabled */}
      {regimeEnabled && (
        <CollapsibleCard
          title="Market Regime"
          icon={<Brain className="w-5 h-5 text-indigo-600" />}
          headerExtra={<span className="text-xs text-gray-400 font-medium ml-2">FINAL ALLOCATION SCORE</span>}
        >
          {regimeScores.length === 0 ? (
            <div className="px-6 py-10 text-center text-gray-500">
              <p className="text-sm">No regime scores available yet. Run the regime bot to generate scores.</p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-100">
                {regimeScores.map((s) => (
                  <div key={`${s.symbol}|${s.config_id}`} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3 min-w-[80px]">
                      <span className="font-bold text-gray-900 text-base">{s.symbol}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-1">
                      <RegimeBadge label={s.regime_label} />
                    </div>
                    <div className="flex items-center gap-1.5 min-w-[110px]">
                      <span className="text-xs text-gray-500 font-medium">Markov</span>
                      <span className={`text-sm font-bold ${s.markov_score >= 0.5 ? 'text-emerald-600' : s.markov_score >= 0 ? 'text-amber-600' : 'text-red-600'}`}>
                        {s.markov_score.toFixed(3)}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1 min-w-[140px] w-full sm:w-40">
                      <ProbBar bull={s.bull_prob} bear={s.bear_prob} />
                      <div className="flex justify-between text-xs text-gray-400">
                        <span className="text-emerald-600 font-medium">{(s.bull_prob * 100).toFixed(0)}% Bull</span>
                        <span className="text-red-500 font-medium">{(s.bear_prob * 100).toFixed(0)}% Bear</span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 whitespace-nowrap">{s.as_of_date}</div>
                  </div>
                ))}
              </div>
              <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex items-center gap-3 flex-wrap">
                <span className="text-xs text-gray-500 font-medium mr-1">Prob bar:</span>
                <span className="flex items-center gap-1 text-xs text-gray-500"><span className="inline-block w-3 h-2 rounded-sm bg-emerald-500" /> Bull</span>
                <span className="flex items-center gap-1 text-xs text-gray-500"><span className="inline-block w-3 h-2 rounded-sm bg-blue-400" /> Sideways</span>
                <span className="flex items-center gap-1 text-xs text-gray-500"><span className="inline-block w-3 h-2 rounded-sm bg-red-500" /> Bear</span>
              </div>
            </>
          )}
        </CollapsibleCard>
      )}

      {/* Recent Rebalance Runs */}
      <CollapsibleCard title="Recent Rebalance Runs">
        {runs.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <RefreshCw className="w-10 h-10 text-gray-400" />
            </div>
            <p className="font-semibold text-gray-600 text-lg">No rebalance runs yet</p>
            <p className="text-base text-gray-400 mt-1">Generate a plan to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {runs.map((run) => (
              <div key={run.id} className="px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-gray-50 transition-all duration-200">
                <div>
                  <p className="font-bold text-gray-900 text-base">{formatRunDateTime(run)}</p>
                  <p className="text-base text-gray-600 mt-1">
                    {run.orders_planned} orders planned, {run.orders_executed} executed
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  {run.equity_before && (
                    <span className="text-base font-semibold text-gray-700">
                      {formatCurrency(run.equity_before)}
                    </span>
                  )}
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold ${
                    run.status === 'completed' ? 'bg-emerald-600 text-white' :
                    run.status === 'failed' ? 'bg-red-600 text-white' :
                    run.status === 'executing' ? 'bg-blue-600 text-white' :
                    'bg-gray-200 text-gray-700'
                  }`}>
                    {run.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleCard>
    </div>
  );
}
